import React, { useEffect, useRef, useState } from 'react';
import { useToast } from '../context/ToastContext';

const LEAD_CONTEXT_KEY = 'lead_context_v1';

const Contact = () => {
    const { addToast } = useToast();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        company: '',
        service: '',
        message: ''
    });

    const MAX_MESSAGE_LEN = 1000;
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState({ type: 'idle', message: '' });
    const statusRef = useRef(null);

    useEffect(() => {
        const raw = window.localStorage.getItem(LEAD_CONTEXT_KEY);
        if (!raw) return;

        try {
            const ctx = JSON.parse(raw);
            setFormData((prev) => ({
                ...prev,
                service: ctx?.service || prev.service,
                message: ctx?.message
                    ? [ctx.message, prev.message].filter(Boolean).join('\n\n')
                    : prev.message
            }));
        } catch {
            // no-op
        } finally {
            window.localStorage.removeItem(LEAD_CONTEXT_KEY);
        }
    }, []);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.id]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isSubmitting) return;
        setIsSubmitting(true);
        setSubmitStatus({ type: 'idle', message: '' });

        try {
            const response = await fetch('http://localhost:5000/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData)
            });

            if (response.ok) {
                addToast('Message received. Our strategy team is reviewing your request.', 'success');
                setFormData({ name: '', email: '', company: '', service: '', message: '' });
                setSubmitStatus({
                    type: 'success',
                    message: 'Message sent. We typically respond within 1-2 business days.'
                });
                requestAnimationFrame(() => statusRef.current?.focus?.());
            } else {
                addToast('Transmission failed. Please verify your connection.', 'error');
                setSubmitStatus({
                    type: 'error',
                    message: 'We could not send your message right now. Please try again shortly.'
                });
                requestAnimationFrame(() => statusRef.current?.focus?.());
            }
        } catch (error) {
            console.error('Error sending message:', error);
            addToast('Network error. Please check your connection.', 'error');
            setSubmitStatus({
                type: 'error',
                message: 'Network error. Please check your connection and try again.'
            });
            requestAnimationFrame(() => statusRef.current?.focus?.());
        } finally {
            setIsSubmitting(false);
        }
    };

    const remaining = MAX_MESSAGE_LEN - (formData.message?.length || 0);
    const showStatus = submitStatus.type !== 'idle';

    return (
        <section id="contact" className="contact-section">
            <div className="container">
                <div className="section-header fade-in">
                    <p className="eyebrow">Contact</p>
                    <h2>Bring us a business problem. We will map the AI path with you.</h2>
                    <p>Tell us what you are trying to improve, automate, predict, or protect.</p>
                </div>

                <div className="contact-container">
                    <div className="contact-form fade-in" aria-busy={isSubmitting ? 'true' : 'false'}>
                        {showStatus && (
                            <div
                                ref={statusRef}
                                tabIndex={-1}
                                role="status"
                                aria-live="polite"
                                className={`form-status form-status--${submitStatus.type}`}
                            >
                                {submitStatus.message}
                            </div>
                        )}

                        <form id="contactForm" onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label htmlFor="name">Full Name</label>
                                <input
                                    type="text"
                                    id="name"
                                    className="form-control"
                                    placeholder="Enter your name"
                                    required
                                    autoComplete="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="email">Email Address</label>
                                <input
                                    type="email"
                                    id="email"
                                    className="form-control"
                                    placeholder="Enter your email"
                                    required
                                    autoComplete="email"
                                    inputMode="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="company">Company</label>
                                <input
                                    type="text"
                                    id="company"
                                    className="form-control"
                                    placeholder="Enter your company name"
                                    autoComplete="organization"
                                    value={formData.company}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="service">Service of Interest</label>
                                <select id="service" className="form-control" value={formData.service} onChange={handleChange}>
                                    <option value="">Select a service</option>
                                    <option value="strategy">AI Strategy & Consulting</option>
                                    <option value="ml">Machine Learning Solutions</option>
                                    <option value="automation">Automation & Optimization</option>
                                    <option value="analytics">Data Analytics</option>
                                    <option value="integration">Custom AI Integration</option>
                                    <option value="training">AI Training & Workshops</option>
                                </select>
                            </div>

                            <div className="form-group">
                                <label htmlFor="message">Message</label>
                                <textarea
                                    id="message"
                                    className="form-control"
                                    rows="5"
                                    placeholder="Tell us about your project or requirements"
                                    required
                                    maxLength={MAX_MESSAGE_LEN}
                                    value={formData.message}
                                    onChange={handleChange}
                                    aria-describedby="messageCount"
                                />
                                <small id="messageCount" className="form-hint">
                                    {remaining} characters left
                                </small>
                            </div>

                            <button type="submit" className="btn-primary" disabled={isSubmitting} aria-disabled={isSubmitting ? 'true' : 'false'}>
                                {isSubmitting ? 'Sending...' : 'Send Message'}
                            </button>
                        </form>
                    </div>

                    <div className="contact-info fade-in">
                        <p className="eyebrow">Consulting Desk</p>
                        <h3>Senior guidance before a line of code is written.</h3>
                        <p className="contact-lede">
                            We respond with a clear next step: discovery call, readiness review, or scoped implementation plan.
                        </p>

                        <div className="contact-details">
                            <div className="contact-item">
                                <div className="contact-icon"><i className="fas fa-map-marker-alt" aria-hidden="true"></i></div>
                                <div>
                                    <h4>Our Headquarters</h4>
                                    <p>123 Innovation Drive, Century City<br />Cape Town, 7441, South Africa</p>
                                </div>
                            </div>
                            <div className="contact-item">
                                <div className="contact-icon"><i className="fas fa-envelope" aria-hidden="true"></i></div>
                                <div>
                                    <h4>Email</h4>
                                    <p>strategy@aiconsultingsa.co.za</p>
                                </div>
                            </div>
                            <div className="contact-item">
                                <div className="contact-icon"><i className="fas fa-clock" aria-hidden="true"></i></div>
                                <div>
                                    <h4>Availability</h4>
                                    <p>Monday to Friday<br />08:30 - 17:00 SAST</p>
                                </div>
                            </div>
                        </div>

                        <div className="map-container">
                            <span className="map-kicker">Cape Town Base</span>
                            <strong>Remote delivery across South Africa and the continent.</strong>
                            <i className="fas fa-map" aria-hidden="true"></i>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Contact;
